import { Link } from 'react-router-dom';
import { siteInfo, testimonials, faqData } from '@/mocks/comics';
import SEOHead from '@/components/feature/SEOHead';

function AboutPage() {
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      const statusEl = form.querySelector('.form-status');
      if (statusEl) {
        statusEl.textContent = '消息已发送，我们会尽快回复！';
        statusEl.className = 'form-status text-sm text-center text-secondary-600 mt-4';
      }
      return;
    }

    const formData = new FormData(form);
    fetch('https://readdy.ai/api/form/d9pem1li2vhsdh2d32p0', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
    })
      .then(async (res) => {
        const text = await res.text();
        let parsed;
        try { parsed = JSON.parse(text); } catch { parsed = null; }
        const statusEl = form.querySelector('.form-status');
        if (res.ok && parsed?.code === 'OK') {
          form.reset();
          if (statusEl) {
            statusEl.textContent = '消息已发送，我们会尽快回复！';
            statusEl.className = 'form-status text-sm text-center text-secondary-600 mt-4';
          }
        } else {
          const msg = parsed?.meta?.message || parsed?.message || text || '发送失败，请稍后重试';
          if (msg.toLowerCase().includes('spam')) {
            if (statusEl) {
              statusEl.textContent = '消息已发送，我们会尽快回复！';
              statusEl.className = 'form-status text-sm text-center text-secondary-600 mt-4';
            }
          } else {
            if (statusEl) {
              statusEl.textContent = msg;
              statusEl.className = 'form-status text-sm text-center text-red-500 mt-4';
            }
          }
        }
      })
      .catch(() => {
        const statusEl = form.querySelector('.form-status');
        if (statusEl) {
          statusEl.textContent = '网络错误，请稍后重试';
          statusEl.className = 'form-status text-sm text-center text-red-500 mt-4';
        }
      });
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '韩漫之家',
        url: 'https://hanmanzhijia.com',
        description: siteInfo.description,
        email: siteInfo.email,
        foundingDate: '2020',
      },
      {
        '@type': 'WebPage',
        name: '关于我们 - 韩漫之家',
        description: '了解韩漫之家 - 国内领先的高清韩国漫画在线阅读平台',
        url: 'https://hanmanzhijia.com/about',
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqData.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们_韩漫之家"
        description="了解韩漫之家 - 国内领先的高清韩国漫画在线阅读平台。品牌介绍、用户口碑、联系方式，韩漫之家致力于为中文读者提供最优质的韩漫阅读体验。"
        keywords="韩漫之家,关于我们,韩国漫画,韩漫,联系方式"
        canonicalUrl="https://hanmanzhijia.com/about"
        jsonLd={aboutJsonLd}
      />

      <div className="pt-16">
        <nav className="max-w-[1200px] mx-auto px-4 md:px-6 pt-6" aria-label="面包屑导航">
          <div className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-home-line mr-0.5"></i>首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="text-foreground-700 font-medium">关于我们</span>
          </div>
        </nav>

        {/* Brand Intro */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                关于韩漫之家
              </h1>
              <p className="text-sm text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                我们热爱韩漫，更热爱分享。韩漫之家汇聚了全网最全的高清韩国漫画资源，致力于为中文读者打造一站式的优质韩漫阅读平台。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mb-14">
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-500">
                  <i className="ri-book-open-line text-2xl"></i>
                </div>
                <p className="text-2xl font-heading font-bold text-foreground-950">{siteInfo.comics}</p>
                <p className="text-xs text-foreground-500 mt-1">漫画作品</p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-accent-100 text-accent-500">
                  <i className="ri-group-line text-2xl"></i>
                </div>
                <p className="text-2xl font-heading font-bold text-foreground-950">{siteInfo.users}</p>
                <p className="text-xs text-foreground-500 mt-1">忠实读者</p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-secondary-100 text-secondary-500">
                  <i className="ri-calendar-check-line text-2xl"></i>
                </div>
                <p className="text-2xl font-heading font-bold text-foreground-950">{siteInfo.founded}</p>
                <p className="text-xs text-foreground-500 mt-1">创立于</p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-500">
                  <i className="ri-star-line text-2xl"></i>
                </div>
                <p className="text-2xl font-heading font-bold text-foreground-950">9.2</p>
                <p className="text-xs text-foreground-500 mt-1">用户评分</p>
              </div>
            </div>

            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4 text-center">
                我们的故事
              </h2>
              <div className="text-sm text-foreground-700 leading-relaxed space-y-4">
                <p>
                  韩漫之家成立于2020年，由一群热爱韩国漫画的年轻团队创建。我们深知韩漫迷们在寻找高质量中文韩漫资源时的困扰——资源分散、更新不及时、翻译质量参差不齐。于是，我们决定打造一个专业、全面、便捷的韩漫在线阅读平台。
                </p>
                <p>
                  经过多年的发展，韩漫之家已经收录了超过{siteInfo.comics}部高清韩国漫画作品，涵盖了浪漫爱情、奇幻冒险、热血动作、搞笑日常、悬疑惊悚、都市剧情、古风历史和BL系列等八大类型。我们与多家韩国漫画平台保持紧密合作，确保作品更新的及时性和翻译的高质量。
                </p>
                <p>
                  如今，韩漫之家已拥有超过{siteInfo.users}忠实读者，成为了中文互联网上最受欢迎的韩漫阅读平台之一。我们始终秉持"让阅读更简单"的理念，不断优化用户体验，让每一位读者都能在这里找到属于自己的精彩故事。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                读者口碑
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                来自真实读者的评价，是我们前进的最大动力
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-11 h-11 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-bold text-foreground-950">{t.name}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    &ldquo;{t.comment}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                有任何问题或建议？欢迎与我们取得联系
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-10 max-w-5xl mx-auto">
              <div className="lg:w-2/5 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-bold text-foreground-950 mb-1">电子邮件</h3>
                    <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-500 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-bold text-foreground-950 mb-1">微信公众号</h3>
                    <p className="text-sm text-foreground-600">{siteInfo.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-500 flex-shrink-0">
                    <i className="ri-weibo-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-bold text-foreground-950 mb-1">官方微博</h3>
                    <p className="text-sm text-foreground-600">{siteInfo.weibo}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-bold text-foreground-950 mb-1">工作时间</h3>
                    <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
              </div>

              <div className="lg:w-3/5">
                <form
                  onSubmit={handleFormSubmit}
                  className="bg-background-100 rounded-xl p-6 md:p-8"
                  data-readdy-form
                  id="contact-form"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      留言
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp-field"
                  />
                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>
                  <div className="form-status text-sm text-center text-foreground-600 mt-4"></div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default AboutPage;